import { useEffect, useState } from "react";
import api, { formatError } from "@/lib/api";
import { PageHeader } from "@/components/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Check, X, BadgeCheck, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

function IdImage({ verificationId }) {
  const [src, setSrc] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let url;
    (async () => {
      try { const res = await api.get(`/admin/verifications/${verificationId}/image`, { responseType: "blob" }); url = URL.createObjectURL(res.data); setSrc(url); }
      catch { setFailed(true); }
    })();
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [verificationId]);
  if (failed) return <div className="w-36 h-24 rounded-lg bg-slate-100 flex items-center justify-center text-[10px] text-slate-400">Image unavailable</div>;
  if (!src) return <div className="w-36 h-24 rounded-lg bg-slate-100 animate-pulse" />;
  return <img src={src} alt="ID" className="w-36 h-24 rounded-lg object-cover border border-slate-200" data-testid={`id-image-${verificationId}`} />;
}

export default function Verifications() {
  const [pending, setPending] = useState([]);
  const [approved, setApproved] = useState([]);

  const load = async () => {
    try {
      const [p, a] = await Promise.all([
        api.get("/admin/verifications", { params: { status: "pending" } }),
        api.get("/admin/verifications", { params: { status: "approved" } }),
      ]);
      setPending(p.data); setApproved(a.data);
    } catch (e) { toast.error(formatError(e)); }
  };
  useEffect(() => { load(); }, []);

  const review = async (id, action) => {
    try { await api.post(`/admin/verifications/${id}/review?action=${action}`); toast.success(`Verification ${action}d`); load(); }
    catch (e) { toast.error(formatError(e)); }
  };

  return (
    <div>
      <PageHeader title="ID Verifications" subtitle="Approve or reject government-ID submissions" />
      <Tabs defaultValue="pending">
        <TabsList>
          <TabsTrigger value="pending" data-testid="adminver-tab-pending">Pending ({pending.length})</TabsTrigger>
          <TabsTrigger value="approved" data-testid="adminver-tab-approved">Approved ({approved.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="pending" className="mt-6"><List items={pending} onReview={review} canAct /></TabsContent>
        <TabsContent value="approved" className="mt-6"><List items={approved} onReview={review} isApproved /></TabsContent>
      </Tabs>
    </div>
  );
}

function List({ items, onReview, canAct, isApproved }) {
  if (!items.length) return <div className="flex flex-col items-center justify-center p-12 border-2 border-dashed border-slate-200 rounded-lg bg-slate-50/50"><ShieldCheck className="w-12 h-12 text-slate-300 mb-3" /><p className="text-slate-500">Nothing here.</p></div>;
  return (
    <div className="space-y-3">
      {items.map((v) => (
        <div key={v.verification_id} className="bg-white rounded-lg border border-slate-200 shadow-sm p-4 sm:p-5" data-testid={`verification-${v.verification_id}`}>
          <div className="flex flex-wrap items-start gap-4">
            <IdImage verificationId={v.verification_id} />
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium text-slate-800">{v.user?.name || "Unknown"}</span>
                {v.user?.is_verified && <BadgeCheck className="w-4 h-4 text-emerald-600" />}
                <span className="text-xs text-slate-400">· {v.user?.district || "—"} · {new Date(v.created_at).toLocaleString()}</span>
              </div>
              <div className="text-xs text-slate-500 mt-1">{v.user?.email}</div>
              <div className="text-sm mt-2 bg-slate-50 rounded-lg p-3 text-slate-600">
                <div>Doc type: <b>{v.verdict?.doc_type || "—"}</b> · Name on ID: <b>{v.verdict?.name_on_doc || "—"}</b></div>
                <div>Name match: <b className={v.name_match ? "text-emerald-700" : "text-red-600"}>{v.name_match ? "Yes" : "No"}</b> · AI: {v.verdict?.reason || "—"}</div>
              </div>
            </div>
            {canAct && (
              <div className="flex flex-wrap gap-2">
                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => onReview(v.verification_id, "approve")} data-testid={`approve-${v.verification_id}`}><Check className="w-3 h-3 mr-1" />Approve</Button>
                <Button size="sm" variant="outline" className="text-red-600 border-red-200" onClick={() => onReview(v.verification_id, "reject")} data-testid={`reject-${v.verification_id}`}><X className="w-3 h-3 mr-1" />Reject</Button>
              </div>
            )}
            {isApproved && (
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" className="text-red-600 border-red-200" onClick={() => onReview(v.verification_id, "reject")} data-testid={`reject-${v.verification_id}`}><X className="w-3 h-3 mr-1" />Reject</Button>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
